<template>
  <v-app>
    <HeaderDealerDashboard 
      :userName="userName" 
      :businessName="businessName"
      @navigate="handleNavigation"
    />
    
    <v-main class="dealer-main">
      <v-container fluid class="pa-6">
        <!-- Page Header -->
        <div class="page-header mb-6">
          <h1 class="text-h4 font-weight-bold text-white mb-0" style="background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%); padding: 20px; border-radius: 8px;">
            <span class="material-icons-round mr-3" style="vertical-align: middle;">account_balance_wallet</span>
            Dealer Portal - Commission
          </h1>
        </div>

        <!-- Current Plan Section -->
        <v-card class="mb-6" elevation="3">
          <v-card-title class="text-h5 font-weight-bold primary--text">
            <span class="material-icons-round mr-2">verified</span>
            Current Plan (Approved)
          </v-card-title>
          <v-card-text class="pa-6">
            <div class="commission-details">
              <div class="detail-item mb-3">
                <strong>Structure:</strong> Flat ${{ currentPlan.amount }} per sold unit
              </div>
              <div class="detail-item mb-3">
                <strong>Effective:</strong> {{ formatDate(currentPlan.effective_date) }}
              </div>
              <div class="detail-item mb-3">
                <strong>Next Review:</strong> {{ formatDate(currentPlan.next_review_date) }}
              </div>
            </div>
            <div class="text-right mt-4">
              <v-btn 
                color="primary" 
                outlined 
                @click="showProposeChangeDialog = true"
                class="text-capitalize"
              >
                <span class="material-icons-round mr-2">edit</span>
                Propose Change
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- Edit Commission Offer Section -->
        <v-card class="mb-6" elevation="3" v-if="showEditForm">
          <v-card-title class="text-h5 font-weight-bold orange--text">
            <span class="material-icons-round mr-2">edit_note</span>
            Edit Commission Offer (Draft)
          </v-card-title>
          <v-card-text class="pa-6">
            <v-form ref="commissionForm" v-model="formValid">
              <v-row>
                <v-col cols="12" md="4">
                  <v-radio-group v-model="draftPlan.structure_type" label="Commission Structure">
                    <v-radio label="Flat Rate" value="flat"></v-radio>
                    <v-radio label="Percentage" value="percentage"></v-radio>
                    <v-radio label="Hybrid" value="hybrid"></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col cols="12" md="8">
                  <div v-if="draftPlan.structure_type === 'flat'">
                    <v-text-field
                      v-model="draftPlan.flat_amount"
                      label="Flat Amount per Unit"
                      prefix="$"
                      type="number"
                      outlined
                      :rules="[rules.required, rules.positive]"
                    ></v-text-field>
                  </div>
                  <div v-if="draftPlan.structure_type === 'percentage'">
                    <v-text-field
                      v-model="draftPlan.percentage"
                      label="Percentage of Selling Price"
                      suffix="%"
                      type="number"
                      outlined
                      :rules="[rules.required, rules.positive]"
                    ></v-text-field>
                  </div>
                  <div v-if="draftPlan.structure_type === 'hybrid'">
                    <v-text-field
                      v-model="draftPlan.base_amount"
                      label="Base Amount"
                      prefix="$"
                      type="number"
                      outlined
                      class="mb-3"
                      :rules="[rules.required, rules.positive]"
                    ></v-text-field>
                    <v-text-field
                      v-model="draftPlan.bonus_percentage"
                      label="Bonus Percentage"
                      suffix="%"
                      type="number"
                      outlined
                      :rules="[rules.required, rules.positive]"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
              
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="draftPlan.notes"
                    label="Additional Notes (Optional)"
                    outlined
                    rows="3"
                  ></v-textarea>
                </v-col>
              </v-row>

              <div class="text-right mt-4">
                <v-btn 
                  color="green" 
                  dark
                  @click="submitForReview"
                  :disabled="!formValid"
                  class="text-capitalize mr-3"
                >
                  <span class="material-icons-round mr-2">send</span>
                  Submit for Admin Review
                </v-btn>
                <v-btn 
                  color="grey" 
                  outlined
                  @click="cancelEdit"
                  class="text-capitalize"
                >
                  Cancel
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- History Section -->
        <v-card elevation="3">
          <v-card-title class="text-h5 font-weight-bold">
            <span class="material-icons-round mr-2">history</span>
            History
          </v-card-title>
          <v-card-text class="pa-6">
            <v-timeline dense>
              <v-timeline-item
                v-for="(item, index) in commissionHistory"
                :key="index"
                :color="item.status === 'Approved' ? 'success' : 'info'"
                small
              >
                <div class="history-item">
                  <div class="font-weight-bold">{{ formatDate(item.date) }}</div>
                  <div>{{ item.description }} ({{ item.status }})</div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>

    <FooterDealer />

    <!-- Propose Change Dialog -->
    <v-dialog v-model="showProposeChangeDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Propose Commission Change</v-card-title>
        <v-card-text>
          <p>Would you like to propose a change to your current commission structure?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showProposeChangeDialog = false">Cancel</v-btn>
          <v-btn color="blue" @click="startEdit">Yes, Propose Change</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '@/components/Layout/HeaderDealerDashboard.vue'
import FooterDealer from '@/components/Layout/FooterDealer.vue'

export default {
  name: 'DealerCommission',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      loading: false,
      userName: '',
      businessName: '',
      showEditForm: false,
      showProposeChangeDialog: false,
      formValid: false,
      currentPlan: {
        structure_type: 'flat',
        amount: 500,
        effective_date: '2025-09-01',
        next_review_date: '2025-12-01'
      },
      draftPlan: {
        structure_type: 'flat',
        flat_amount: 500,
        percentage: 3.0,
        base_amount: 200,
        bonus_percentage: 2.0,
        notes: ''
      },
      commissionHistory: [
        {
          date: '2025-08-10',
          description: 'Changed from 3% to Flat $500',
          status: 'Approved'
        },
        {
          date: '2025-05-02',
          description: 'Updated tiers',
          status: 'Approved'
        }
      ],
      rules: {
        required: value => !!value || 'This field is required',
        positive: value => value > 0 || 'Value must be greater than 0'
      }
    }
  },
  beforeCreate() {
    // Check authentication
    if (!localStorage.getItem('token') || localStorage.getItem('user_flag') !== 'dealer') {
      this.$router.push('/dealers/login')
    }
  },
  async mounted() {
    await this.loadUserData()
    await this.loadCommissionData()
  },
  methods: {
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Business'
    },
    async loadCommissionData() {
      try {
        this.loading = true
        const token = localStorage.getItem('token')
        
        const response = await axios.get('/api/dealer/commission-data', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.currentPlan = response.data.data.current_plan || this.currentPlan
          this.commissionHistory = response.data.data.history || this.commissionHistory
        }
      } catch (error) {
        console.error('Error loading commission data:', error)
        this.$toast.error('Error loading commission data')
      } finally {
        this.loading = false
      }
    },
    handleNavigation(item) {
      // Navigation is handled by the HeaderDealerDashboard component
      // This method is kept for compatibility but doesn't need to do anything
      // since the header component now handles routing directly
    },
    startEdit() {
      this.showProposeChangeDialog = false
      this.showEditForm = true
    },
    cancelEdit() {
      this.showEditForm = false
      this.resetDraftPlan()
    },
    resetDraftPlan() {
      this.draftPlan = {
        structure_type: 'flat',
        flat_amount: 500,
        percentage: 3.0,
        base_amount: 200,
        bonus_percentage: 2.0,
        notes: ''
      }
    },
    async submitForReview() {
      if (!this.$refs.commissionForm.validate()) {
        return
      }

      try {
        this.loading = true
        const token = localStorage.getItem('token')
        
        const response = await axios.post('/api/dealer/propose-commission-change', this.draftPlan, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Commission change proposal submitted successfully!')
          this.showEditForm = false
          this.resetDraftPlan()
          await this.loadCommissionData()
        } else {
          this.$toast.error(response.data.message || 'Error submitting proposal')
        }
      } catch (error) {
        console.error('Error submitting commission proposal:', error)
        this.$toast.error('Error submitting commission proposal')
      } finally {
        this.loading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })
    }
  }
}
</script>

<style scoped>
.dealer-main {
  background-color: #f5f5f5;
  min-height: 100vh;
}

.page-header h1 {
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.commission-details .detail-item {
  font-size: 16px;
  line-height: 1.6;
}

.history-item {
  padding: 8px 0;
}

.v-card {
  border-radius: 12px !important;
}

.v-card-title {
  padding: 20px 24px 16px 24px;
}

.v-card-text {
  padding: 0 24px 24px 24px;
}
</style>
